<script setup lang="ts">
import { SelectedLocation } from '~/lib/interfaces/restaurant'
import { ToastType } from '~/lib/models/ui/toast'
import { getPermissionStatus, getUserLocation } from '~/utils/geoLocationUtils'
import { isAutocompletePrediction } from '~/lib/interfaces/helpers'

const props = defineProps<{
  modal?: boolean
  restaurantList?: boolean
  absolutePlaces?: boolean
  openSearch?: boolean
  searchScreen?: boolean
}>()

const emit = defineEmits(['on-search', 'search-mobile'])
const appStore = useAppStore()
const restaurantStore = useRestaurantStore()
const { fetchListRestaurants } = useRestaurantsMOP()
const { places, searchPlaces, resetPlaces } = useGooglePlaces()
const { $toast } = useNuxtApp()
const { t } = useLocale()
const { trackGeolocate } = useGTM()

const searchQuery = ref()
const inputRef = ref<HTMLInputElement>()

const disableSearch = computed(() => {
  if (!appStore.initialFetchLoaded) return true
  if (props.restaurantList) return false
  return !restaurantStore.candidateArea
})

const handleFocus = () => {
  if (props.openSearch) {
    emit('search-mobile')
    return
  }
  if (searchQuery.value) {
    searchPlaces(searchQuery.value)
  }
}

watch(searchQuery, newSearchInput => {
  searchPlaces(newSearchInput)
})

const setSelectedLocation = async (location: SelectedLocation) => {
  useGoogleSessionToken()
  const address = await getAddressInfo(location.place_id)
  if (!address) return
  if (!props.restaurantList) restaurantStore.setAddress(address)

  const addressOfficial = `${address.address ?? ''} ${address.number ?? ''}`.trim()

  const hasProperty =
    isAutocompletePrediction(location, 'description') &&
    isAutocompletePrediction(location, 'structured_formatting')

  const finalLocation = hasProperty
    ? {
        ...location,
        description: addressOfficial,
        structured_formatting: {
          ...location.structured_formatting,
          main_text: addressOfficial,
        },
      }
    : location

  restaurantStore.setGoogleLocation({
    ...finalLocation,
    lat: address.latitude,
    lng: address.longitude,
  })
  resetPlaces()
  restaurantStore.openModal()
  emit('on-search')
  highlightedIndex.value = null
}

const searchFromLocation = async () => {
  if (process.client) {
    trackGeolocate()
    const permissionStatus = await getPermissionStatus('geolocation')
    if (permissionStatus === 'denied' || permissionStatus === null) {
      const isDenied = permissionStatus === 'denied'
      const toastConfig = {
        type: isDenied ? ToastType.WARNING : ToastType.ERROR,
        content: t(`errors.toast.geo.${isDenied ? 'denied' : 'unsupported'}`),
      }
      $toast.show(toastConfig)
      return
    }

    try {
      const userLocation = await getUserLocation()

      const geoCoder = new google.maps.Geocoder()
      const searchBox: google.maps.GeocoderRequest = {
        location: userLocation,
      }

      geoCoder.geocode(searchBox, async (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
          $toast.show({
            type: ToastType.ERROR,
            content: t('errors.toast.geo.geocoderError'),
          })
          return
        }

        const place = results.find(place => place.types.includes('street_address')) || results[0]
        setSelectedLocation(place)
      })
    } catch (_) {
      $toast.show({
        type: ToastType.ERROR,
        content: t('errors.toast.geo.unexpectedError'),
      })
    }
  }
}

const highlightedIndex = ref<null | number>(null)

const resetHighlightedIndex = () => {
  highlightedIndex.value = null
}

const handleKeyDown = () => {
  if (highlightedIndex.value === null) {
    highlightedIndex.value = 0
    return
  }
  if (highlightedIndex.value === places.value.length - 1) {
    highlightedIndex.value = 0
    return
  }
  highlightedIndex.value += 1
}

const handleKeyUp = () => {
  if (highlightedIndex.value === null) {
    highlightedIndex.value = 0
    return
  }
  if (highlightedIndex.value === 0) {
    highlightedIndex.value = places.value.length - 1
    return
  }
  highlightedIndex.value -= 1
}

const handleTab = (e: KeyboardEvent) => {
  if (e.key === 'Tab') {
    e.preventDefault()
    e.stopPropagation()
    handleKeyDown()
  }
}

const handleOnEnter = () => {
  if (props.openSearch) {
    emit('search-mobile')
    return
  }
  if (places.value.length > 0) {
    setSelectedLocation(places.value[highlightedIndex.value || 0])
  }
}

defineExpose({
  searchFromLocation,
})

onMounted(async () => {
  if (process.client) {
    await fetchListRestaurants()
    if (!props.openSearch) {
      inputRef.value?.focus()
    }
  }
})
</script>

<template>
  <div class="m-auto flex w-full flex-wrap justify-center gap-2 md:m-0">
    <div class="flex w-full gap-2">
      <div class="relative mx-4 w-[600px] md:mx-0">
        <div>
          <section class="relative">
            <!-- Search icon only on search screen -->
            <button
              v-if="searchScreen"
              class="absolute left-0 top-1/2 ml-2 h-fit -translate-y-1/2 cursor-pointer"
            >
              <img src="/images/icons/search.svg" alt="search" />
            </button>

            <!-- Input -->
            <input
              ref="inputRef"
              v-model="searchQuery"
              class="h-8 w-full rounded-md pl-4 pr-8 placeholder-secondaryDarkGrey focus:placeholder-opacity-0 disabled:cursor-not-allowed disabled:placeholder-opacity-50 md:h-10"
              type="text"
              data-test="input-search-bar"
              :placeholder="t('home.search')"
              :class="{
                'bg-mcd-secondaryIvory': modal || restaurantList,
                'md:h-8': restaurantList,
                'bg-mcd-secondaryIvory !pl-10 outline-none': searchScreen,
              }"
              :disabled="!!disableSearch"
              @keydown.enter="handleOnEnter"
              @keydown.down="handleKeyDown"
              @keydown.up="handleKeyUp"
              @keydown.tab="e => handleTab(e)"
              @focus="handleFocus"
            />

            <!-- LocateMe button inside input, not on search screen -->
            <button
              v-if="!searchScreen"
              class="absolute right-0 top-1/2 mr-2 h-fit -translate-y-1/2 cursor-pointer"
              @click="searchFromLocation"
            >
              <img src="/images/icons/locateMe.svg" alt="locateMe" />
            </button>
          </section>

          <!-- Places dropdown -->
          <LazySearchDropDown
            v-if="searchQuery && places.length"
            :class="{ 'absolute left-0 top-10': absolutePlaces }"
            :places="places"
            :set-selected-location="setSelectedLocation"
            :highlighted-index="highlightedIndex"
            :reset-highlighted-index="resetHighlightedIndex"
          />
        </div>
      </div>
      <McdButton
        :is-secondary="modal"
        :is-primary="!modal"
        class="hidden h-10 rounded-md px-6 py-0 md:block"
        :class="{
          'h-8': restaurantList,
        }"
        @click="handleOnEnter"
      >
        <p class="text-sm">{{ t('home.searchButton') }}</p>
      </McdButton>
    </div>
  </div>
</template>
